import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconDots, IconPlus } from '@tabler/icons-react';
import ArtPreview from '../ArtPreview';
import { maxPages, pageIndex } from '../../../model/operations';
import { gsap, reducedMotion } from '../../motion';
import './styles.scss';

const OPEN_DELAY = 60;
const CLOSE_DELAY = 220;
const narrow = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches;

// One page as a document of its own, so its thumbnail draws again only when that page
// changed (operations keep untouched pages by reference).
const PageThumb = memo(function PageThumb({ doc, page }) {
  const single = useMemo(() => ({ ...doc, pages: [page] }), [page, doc.width, doc.height]); // eslint-disable-line react-hooks/exhaustive-deps
  return <ArtPreview document={single} pageId={page.id} />;
});

// The carousel's pages, at the foot of the action rail. At rest it takes the room of a
// small summary: with more than two pages, a dot per page and the page on the stage as a
// miniature; with one or two, their miniatures. Hovering it (a tap on touch screens) grows
// the gallery of every page over the rail, without moving anything (GSAP); a page opens
// on a click, drags to another place (Alt + ↑/↓ from the keyboard), and its ⋯ (or a
// right-click) duplicates, moves or deletes it. Hovering the gap between two pages shows a
// "+" that adds one there. Nothing in the gallery edits a read-only art.
export default function PageNavigator({ doc, currentId, onSelect, onAdd, onMove, onMenu, readOnly = false }) {
  const { t } = useTranslation('artEditor');
  const root = useRef(null);
  const summary = useRef(null);
  const panel = useRef(null);
  const list = useRef(null);
  const timer = useRef(null);
  const pinned = useRef(false);
  const drag = useRef(null);
  const swallow = useRef(false);
  const pointer = useRef('mouse');
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [dragging, setDragging] = useState(false);
  const pages = doc.pages;
  const count = pages.length;
  const index = pageIndex(doc, currentId);
  const full = count >= maxPages(doc);
  const compact = count > 2;
  const horizontal = narrow();
  const axis = horizontal ? 'x' : 'y';

  const show = useCallback((delay = OPEN_DELAY) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), delay); }, []);
  const hide = useCallback((delay = CLOSE_DELAY) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (!drag.current?.moved && !pinned.current) setOpen(false); }, delay);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (open) setShown(true); }, [open]);

  // A press outside (and outside the menu it opened) closes the gallery.
  useEffect(() => {
    if (!open) return undefined;
    const away = event => {
      if (root.current?.contains(event.target) || event.target.closest?.('.custom-popper-portal')) return;
      pinned.current = false;
      setOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [open]);

  // The gallery grows out of the summary (its clip opens from the summary's corner) and its
  // pages rise in one after another; closing folds it back.
  useLayoutEffect(() => {
    const element = panel.current;
    if (!shown || !element) return undefined;
    const quick = reducedMotion();
    if (open) {
      const rail = root.current.closest('.art-actions')?.getBoundingClientRect();
      const own = root.current.getBoundingClientRect();
      // Up to the rail's top (a column) or, on phones, from the screen's edge to the summary's right.
      if (rail) element.style.setProperty('--art-pages-room', `${Math.max(160, horizontal ? own.right - 8 : own.bottom - rail.top)}px`);
      list.current?.querySelector('.is-current')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
    const box = element.getBoundingClientRect();
    const small = summary.current.getBoundingClientRect();
    const width = Math.max(0, box.width - small.width);
    const height = Math.max(0, box.height - small.height);
    // Both ends spelled the same way: the browser shortens a read-back `inset()`, which would
    // pair the wrong numbers mid-tween.
    const folded = horizontal ? `inset(0px 0px ${height}px ${width}px round 16px)` : `inset(${height}px 0px 0px ${width}px round 16px)`;
    const unfolded = 'inset(0px 0px 0px 0px round 16px)';
    if (open) {
      const timeline = gsap.timeline();
      timeline.fromTo(element, { clipPath: folded, autoAlpha: 0.5 }, { clipPath: unfolded, autoAlpha: 1, duration: quick ? 0 : 0.28, ease: 'power3.out' })
        .fromTo(element.querySelectorAll('.art-pages__row'), { autoAlpha: 0, [axis]: 10 }, { autoAlpha: 1, [axis]: 0, duration: quick ? 0 : 0.2, stagger: quick ? 0 : 0.03, ease: 'power2.out', clearProps: 'transform' }, quick ? 0 : 0.05);
      return () => timeline.kill();
    }
    const animation = gsap.fromTo(element, { clipPath: unfolded }, { clipPath: folded, autoAlpha: 0, duration: quick ? 0 : 0.16, ease: 'power2.in', onComplete: () => setShown(false) });
    return () => animation.kill();
  }, [open, shown]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = id => {
    if (swallow.current) { swallow.current = false; return; }
    onSelect(id);
    pinned.current = false;
    setOpen(false);
  };
  const menuAt = (page, x, y, event) => {
    if (readOnly || !onMenu) return;
    event?.preventDefault();
    pinned.current = true;
    onMenu(page, x, y);
  };

  // Drag to reorder: past a few pixels the page follows the pointer and the others make
  // room (the layer list's gesture); the list scrolls when the pointer nears its ends.
  const at = event => (horizontal ? event.clientX : event.clientY);
  const scrolled = () => (horizontal ? list.current.scrollLeft : list.current.scrollTop);
  const onPointerDown = (event, position) => {
    pointer.current = event.pointerType;
    if (readOnly || event.button !== 0 || !onMove) return;
    drag.current = { position, to: position, start: at(event), moved: false, id: event.pointerId, element: event.currentTarget };
  };
  const onPointerMove = event => {
    const current = drag.current;
    if (!current) return;
    const delta = at(event) - current.start;
    if (!current.moved) {
      if (Math.abs(delta) < 5) return;
      current.moved = true;
      current.element.setPointerCapture?.(current.id);
      current.rows = [...list.current.querySelectorAll('.art-pages__item:not(.is-end)')];
      const first = current.rows[0].getBoundingClientRect();
      const second = current.rows[1]?.getBoundingClientRect();
      current.step = second ? (horizontal ? second.left - first.left : second.top - first.top) : (horizontal ? first.width : first.height);
      current.scroll = scrolled();
      current.rows[current.position].classList.add('is-lifted');
      setDragging(true);
    }
    const bounds = list.current.getBoundingClientRect();
    const near = horizontal ? [bounds.left, bounds.right] : [bounds.top, bounds.bottom];
    if (at(event) < near[0] + 28) list.current[horizontal ? 'scrollLeft' : 'scrollTop'] -= 10;
    if (at(event) > near[1] - 28) list.current[horizontal ? 'scrollLeft' : 'scrollTop'] += 10;
    const offset = delta + (scrolled() - current.scroll);
    const to = Math.max(0, Math.min(current.rows.length - 1, current.position + Math.round(offset / current.step)));
    gsap.set(current.rows[current.position], { [axis]: offset });
    if (to !== current.to) {
      current.to = to;
      current.rows.forEach((row, position) => {
        if (position === current.position) return;
        const shift = current.position < position && position <= to ? -current.step : to <= position && position < current.position ? current.step : 0;
        gsap.to(row, { [axis]: shift, duration: reducedMotion() ? 0 : 0.16, ease: 'power2.out', overwrite: 'auto' });
      });
    }
  };
  const onPointerUp = event => {
    const current = drag.current;
    drag.current = null;
    if (!current?.moved) return;
    current.element.releasePointerCapture?.(event.pointerId);
    gsap.killTweensOf(current.rows);
    gsap.set(current.rows, { clearProps: 'transform' });
    current.rows[current.position].classList.remove('is-lifted');
    setDragging(false);
    swallow.current = true;
    setTimeout(() => { swallow.current = false; }, 0);
    if (current.to !== current.position) onMove(pages[current.position].id, current.to);
  };

  const onKeyDown = (event, page, position) => {
    const back = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const ahead = horizontal ? 'ArrowRight' : 'ArrowDown';
    if (event.key !== back && event.key !== ahead) return;
    event.preventDefault();
    const target = position + (event.key === back ? -1 : 1);
    if (target < 0 || target >= count) return;
    if (event.altKey && !readOnly && onMove) onMove(page.id, target);
    else list.current.querySelectorAll('.art-pages__thumb')[target]?.focus();
  };

  const gap = position => (!readOnly && !full && onAdd
    ? <div className="art-pages__gap">
      <Button className="art-pages__add" size="micro" variant="rounded" color="inverse" preventLoading icon={<IconPlus />} aria-label={t('addPageAt', { number: position + 1 })}
        tooltip={t('addPage')} onClick={() => { pinned.current = false; setOpen(false); onAdd(position); }} />
    </div>
    : <div className="art-pages__gap is-idle" />);

  return <div ref={root} className={`art-pages ${horizontal ? 'is-horizontal' : ''}`}
    onPointerEnter={event => { if (event.pointerType === 'mouse') { pinned.current = false; show(); } }}
    onPointerLeave={event => { if (event.pointerType === 'mouse') hide(); }}
    onKeyDown={event => { if (event.key === 'Escape' && open) { event.stopPropagation(); pinned.current = false; setOpen(false); summary.current?.focus(); } }}>
    <button ref={summary} type="button" className="art-pages__summary" aria-expanded={open} aria-label={t('pagesSummary', { number: index + 1, count })}
      onPointerDown={event => { pointer.current = event.pointerType; }}
      onClick={() => { if (pointer.current === 'mouse') show(0); else if (open) setOpen(false); else show(0); }}>
      <span className="art-pages__stack" aria-hidden="true">
        {pages.map((page, position) => (compact && position !== index
          ? <span key={page.id} className="art-pages__dot" />
          : <span key={page.id} className={`art-pages__mini ${position === index ? 'is-current' : ''}`}><PageThumb doc={doc} page={page} /></span>))}
      </span>
      <Text.caption contrast={6} className="art-pages__count">{index + 1}/{count}</Text.caption>
    </button>
    {shown && <div ref={panel} className="art-pages__panel" role="group" aria-label={t('pages')}>
      <ol ref={list} className={`art-pages__list ${dragging ? 'is-dragging' : ''}`}>
        {pages.map((page, position) => <li key={page.id} className={`art-pages__item ${position === index ? 'is-current' : ''}`}>
          {gap(position)}
          <div className="art-pages__row">
            <Text.caption contrast={position === index ? 9 : 6} className="art-pages__number">{position + 1}</Text.caption>
            <button type="button" className="art-pages__thumb" aria-current={position === index ? 'page' : undefined} aria-label={t('pageNumber', { number: position + 1 })}
              onPointerDown={event => onPointerDown(event, position)} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
              onClick={() => choose(page.id)} onKeyDown={event => onKeyDown(event, page, position)} onContextMenu={event => menuAt(page, event.clientX, event.clientY, event)}>
              <PageThumb doc={doc} page={page} />
            </button>
            {!readOnly && onMenu && <Button className="art-pages__more" size="micro" variant="rounded" color="current" preventLoading icon={<IconDots />}
              aria-label={t('pageActions', { number: position + 1 })}
              onClick={event => { const rect = event.currentTarget.getBoundingClientRect(); menuAt(page, rect.left, rect.bottom + 4); }} />}
          </div>
        </li>)}
        <li className="art-pages__item is-end">{gap(count)}</li>
      </ol>
    </div>}
  </div>;
}
